<template>
    <div class="lms__superadmin_workspaces_edit_apiintegration">
        <div class="columns">
            <div class="column is-narrow">
                <div class="columns is-vcentered">
                    <div class="column is-narrow">
                        <form-checkbox
                            :class="'lms__superadmin_workspaces_edit_apiintegration_checkboxactive'"
                            :label="''"
                            :name="'lms-superadmin-workspaces-edit-apiintegration-checkboxactive'"
                            :switcher="true"
                            :disabled="handleApiIntegrationStatusRequestPending || (!customerHasActiveAPIIntegrationService && !workspaceApiIntegrationState)"
                            :switcher-text-active="'API enabled'"
                            :switcher-text-inactive="'API disabled'"
                            :value="workspaceApiIntegrationState"
                            :prevent="true"
                            v-on="workspaceApiIntegrationState ? { 'input': performDisableApiIntegration } : { 'input': openNotifyEnableApiIntegration }"
                        >
                        </form-checkbox>
                    </div>
                    <div class="column">
                        <component-button
                            :name="'lms-superadmin-workspaces-edit-apiintegration-apilink'"
                            :text="true"
                            :underline="true"
                            :to-external="`${ api.euma }/api/doc`"
                            :size="'small'"
                            :target="true"
                        >API documentation</component-button>
                    </div>
                </div>
            </div>
            <div class="column"></div>
            <div class="column is-narrow">
                <component-button
                    :name="'lms-superadmin-workspace-edit-api-integration-generate-key'"
                    :animate-loading="true"
                    :animate-loading-event-name="'api-integration-generate-key'"
                    :disabled="workspaceApiIntegrationKeysAmountOfActive >= 2 || handleApiIntegrationStatusRequestPending"
                    :hint="workspaceApiIntegrationKeysAmountOfActive >= 2 ? 'There can be only 2 active keys' : ''"
                    @click="handleGenerateKey"
                >Generate key</component-button>
            </div>
        </div>
        <component-list
            ref="apiintegrationlist"
            :class="[
                'lms__superadmin_workspaces_edit_apiintegration_list',
                !workspaceApiIntegrationState ? 'lms__superadmin_workspaces_edit_apiintegration_list--apiintegrationdisabled' : '',
                workspaceApiIntegrationKeys.length ? 'lms__superadmin_workspaces_edit_apiintegration_list--hasresults' : ''
            ]"
            :component-table="LmsSuperadminWorkspaceEditApiIntegrationTable"
            :component-table-name="'keys'"
            :get-items-getter="'getLmsSuperadminWorkspaceApiIntegrationKeysData'"
            :get-items-dispatch="'lmsSuperadminWorkspaceApiIntegrationKeysGet'"
            :get-items-dispatch-data="{ workspaceId }"
            :item-name="['key', 'keys']"
            :filters-active="false"
            :empty-state-title="workspaceApiIntegrationState ? `Great! API is now enabled for this workspace.\n\nGo ahead and generate a key to use the API.` : `API integration is disabled for this workspace.\n\nClick the toggle to enable the API integration for this workspace.`"
            :empty-state-button-active="workspaceApiIntegrationState"
            :empty-state-button-text="'Generate key'"
            @emit-edit="handleEditKey"
            @emit-empty-state-submit="handleGenerateKey"
        ></component-list>
        <pop-notify
            ref="notifyenableapi"
            :class="'pop__notify--enableapi'"
            :title-text="'Enable API'"
            :content-text="'Turning API on deletes existing organisation structure. It is recommended to create organisation units using API before inviting learners.'"
            :cancel-button-text="'Cancel'"
            :cancel-button-active="true"
            :submit-button-text="'Confirm'"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            :size="'medium'"
            @emit-submit="performEnableApiIntegration"
        ></pop-notify>
        <pop-notify
            ref="notifyeditkey"
            :class="'pop__notify--edit'"
            :title-text="'Generate key'"
            :content-text="''"
            :cancel-button-text="'Close'"
            :cancel-button-active="true"
            :submit-button-active="false"
            :size="'medium'"
        >
            <component-text>Copy the key below and hand it to the customer to activate the API.</component-text>
            <component-title :size="'label'">Key</component-title>
            <div class="columns">
                <div class="column">
                    <component-text>{{ workspaceApiIntegrationKeyName }}</component-text>
                </div>
                <div class="column is-narrow">
                    <component-button
                        :name="'lms-superadmin-workspace-edit-apiintegration-copy-apikey'"
                        :icon="true"
                        :icon-name="'copy'"
                        :icon-raw="true"
                        :theme="''"
                        @click="handleCopyToClipboard(workspaceApiIntegrationKeyApiKey, 'key')"
                    >
                    </component-button>
                </div>
            </div>
            <form-checkbox
                :name="'lms-superadmin-workspaces-edit-apiintegration-edit-status'"
                :label="'Status'"
                :switcher="true"
                :disabled="popNotifySuspendRequestPending"
                :value="workspaceApiIntegrationKeyStatus"
                @input="performSuspendKey"
            ></form-checkbox>
        </pop-notify>
    </div>
</template>

<script>
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentList from '@/components/default/shared/ComponentList'
import LmsSuperadminWorkspaceEditApiIntegrationTable from '@/components/lms/superadmin/workspace/edit/LmsSuperadminWorkspaceEditApiIntegrationTable'
import ComponentEmptyState from '@/components/default/shared/ComponentEmptyState'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentText from '@/components/default/shared/ComponentText'
import FormText from '@/components/default/forms/FormText'
import ComponentNote from '@/components/default/shared/ComponentNote'
import api from '@/api'

export default {
  name: 'lms-superadmin-workspace-edit-api-integration',
  components: { ComponentNote, FormText, ComponentText, ComponentButton, ComponentEmptyState, ComponentList, PopNotify, FormCheckbox, ComponentTitle },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  data () {
    return {
      api,
      user: new User(),
      getKeyRequestPending: false,
      handleApiIntegrationStatusRequestPending: false,
      popNotifyRerenderIndex: 0,
      popNotifyProcess: 'add',
      popNotifySuspendRequestPending: false,
      LmsSuperadminWorkspaceEditApiIntegrationTable
    }
  },
  computed: {
    customerHasActiveAPIIntegrationService () {
      return Boolean(this.$store.getters.getLmsSuperadminCustomerSubscriptionsActiveByKey('API'))
    },
    customerHasAPIIntegrationService () {
      return Boolean(this.$store.getters.getLmsSuperadminCustomerSubscriptionsByKey('API'))
    },
    workspaceId () {
      return this.$route.params.id
    },
    workspaceCustomerId () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomerId
    },
    workspaceApiIntegrationState () {
      return this.$store.getters.getLmsSuperadminWorkspaceApiIntegrationState
    },
    workspaceApiIntegrationKeys () {
      return this.$store.getters.getLmsSuperadminWorkspaceApiIntegrationKeysData
    },
    workspaceApiIntegrationKeysAmountOfActive () {
      return this.workspaceApiIntegrationKeys.filter(obj => obj.status === 'active').length
    },
    workspaceApiIntegrationKeyId () {
      return this.$store.getters.getLmsSuperadminWorkspaceApiIntegrationKeyId
    },
    workspaceApiIntegrationKeyName () {
      const apiKey = JSON.parse(JSON.stringify(this.workspaceApiIntegrationKeyApiKey))
      let apiKeyArr = apiKey.split('')

      for (let index in apiKeyArr) {
        const indexNumber = Number(index) + 1
        const arrLength = apiKeyArr.length

        if (indexNumber > 7) {
          apiKeyArr[index] = 'x'
        }

        if (indexNumber && indexNumber % 5 === 0 && indexNumber !== arrLength) {
          apiKeyArr[index] += ' - '
        }
      }

      const apiKeyOutput = apiKeyArr.join('')

      return apiKeyOutput
    },
    workspaceApiIntegrationKeyStatus () {
      return this.$store.getters.getLmsSuperadminWorkspaceApiIntegrationKeyStatus
    },
    workspaceApiIntegrationKeyApiKey () {
      return this.$store.getters.getLmsSuperadminWorkspaceApiIntegrationKeyApiKey
    }
  },
  methods: {
    getWorkspaceApiIntegrationState () {
      this.handleApiIntegrationStatusRequestPending = true
      this.$store.dispatch('lmsSuperadminWorkspaceGetApiIntegrationState', this.workspaceId).finally(() => {
        this.handleApiIntegrationStatusRequestPending = false
      })
    },
    performDisableApiIntegration () {
      this.handleApiIntegrationStatusRequestPending = true
      this.$store.dispatch('lmsSuperadminWorkspaceDeactivateApiIntegration').finally(() => {
        this.handleApiIntegrationStatusRequestPending = false
      })
    },
    openNotifyEnableApiIntegration () {
      this.$refs.notifyenableapi.openNotify()
    },
    closeNotifyEnableApiIntegration () {
      this.$refs.notifyenableapi.closeNotify()
    },
    performEnableApiIntegration () {
      this.handleApiIntegrationStatusRequestPending = true
      this.$store.dispatch('lmsSuperadminWorkspaceActivateApiIntegration').finally(() => {
        this.handleApiIntegrationStatusRequestPending = false
        this.closeNotifyEnableApiIntegration()
      })
    },
    handleGenerateKey () {
      this.$store.dispatch('lmsSuperadminWorkspaceApiIntegrationKeyCreate', this.workspaceId).then(() => {
        this.$refs.apiintegrationlist.getItems()
      }).finally(() => {
        this.$eventBus.$emit('stop-api-integration-generate-key')
      })
    },
    handleEditKey (data) {
      const apiIntegrationKeys = this.workspaceApiIntegrationKeys
      const selectedKey = apiIntegrationKeys.find(obj => obj.id === data)

      this.$store.dispatch('lmsSuperadminWorkspaceApiIntegrationKeySet', selectedKey)
      this.$refs.notifyeditkey.openNotify()
    },
    performSuspendKey () {
      this.popNotifySuspendRequestPending = true
      this.$store.dispatch('lmsSuperadminWorkspaceApiIntegrationKeySuspend', this.workspaceApiIntegrationKeyId).then(() => {
        this.$refs.notifyeditkey.closeNotify()
        this.$refs.apiintegrationlist.getItems()
      }).finally(() => {
        this.popNotifySuspendRequestPending = false
      })
    }
  },
  mounted () {
    this.getWorkspaceApiIntegrationState()
    if (!this.customerHasAPIIntegrationService) {
      this.$router.push({ name: 'lms-superadmin-workspace-edit-details' })
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddeditapiintegration.scss";
</style>
